import InventoryImagePlaceholder from "../InventoryImagePlaceholder";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  FiRefreshCcw,
  FiPlus,
  FiList,
  FiGrid,
  FiEdit,
  FiTrash2,
  FiSearch,
  FiX,
  FiMinus,
} from "react-icons/fi";

import SkeletonLoader from "../Loader/SkeletonLoader";
import { useWindowSize } from "../../hooks/useWindowSize";
import { Link } from "react-router-dom";
import AddInventoryModal, {
  InventoryFormData,
} from "../../screen/Inventory/AddInventoryModal";
import UploadButton from "../Buttons/UploadButton";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store/store";
import { deleteInventoryItem } from "../../store/inventory/actions";
import { uploadInventoryImage } from "../../services/inventoryApi";
import ConfirmDeleteModal from "../../screen/Inventory/ConfirmDeleteModal";
import CategoryChips from "../CategoryChips";
import ImageViewerModal from "../ImageViewer";

export interface Document {
  _id: string;
  itemName: string;
  quantity: number;
  costPerUnit?: number;
  defaultSellingPrice?: number;
  imageUrl?: string;
  brand?: string;
  category?: string;
  categoryName?: string;
  categoryId?: string | { _id?: string; name?: string; scope?: string };
  businessName?: string;
  notes?: string;
  status: "active" | "discontinued" | "out of stock";
  sku?: string;
  variants?: Array<{ attribute: string; value: string }>;
  lowStock?: {
    enabled?: boolean;
    threshold?: number;
  };
  lowStockEnabled?: boolean;
  lowStockThreshold?: number;
  __isLowStock?: boolean;
}
interface DocumentsListProps {
  documentsList: Document[];
  isLoading: boolean;
  viewMode: "list" | "card";
  currentPage: number;
  totalCount: number;
  perPage: number;
  onPageChange: (page: number) => void;
  categories: string[];
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  inventoryFilter: "all" | "outOfStock" | "bestSeller" | "lowStock";
  onInventoryFilterChange: (
    filter: "all" | "outOfStock" | "bestSeller" | "lowStock",
  ) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  onToggleView: () => void;
  onEditItem: (item: Document) => void;
  onDuplicateItem: (item: Document) => void;
  allowAddInventory?: boolean;
  addInventoryLabel?: string;
  allowItemActions?: boolean;
  quantityEditMode?: boolean;
  getStagedQuantity?: (item: Document) => number;
  getOriginalQuantity?: (item: Document) => number;
  isQuantityDirty?: (item: Document) => boolean;
  isQuantityConflicted?: (item: Document) => boolean;
  onIncrementQuantity?: (item: Document) => void;
  onDecrementQuantity?: (item: Document) => void;
  onQuantityInput?: (item: Document, value: number) => void;
  onUseCurrentQuantity?: (item: Document) => void;
  onKeepPendingQuantity?: (item: Document) => void;
}

const DocumentsList = ({
  documentsList,
  isLoading,
  viewMode,
  onPageChange,
  currentPage,
  totalCount,
  perPage,
  categories,
  selectedCategory,
  onCategoryChange,
  searchTerm,
  onSearchChange,
  inventoryFilter,
  onInventoryFilterChange,
  onRefresh,
  isRefreshing,
  onToggleView,
  onEditItem,
  allowAddInventory = true,
  addInventoryLabel = "Add Inventory",
  allowItemActions = true,
  quantityEditMode = false,
  getStagedQuantity = (item) => Number(item.quantity) || 0,
  getOriginalQuantity = (item) => Number(item.quantity) || 0,
  isQuantityDirty = () => false,
  isQuantityConflicted = () => false,
  onIncrementQuantity = () => undefined,
  onDecrementQuantity = () => undefined,
  onQuantityInput = () => undefined,
  onUseCurrentQuantity = () => undefined,
  onKeepPendingQuantity = () => undefined,
}: DocumentsListProps) => {
  const inventoryFilterOptions: Array<{
    value: "all" | "outOfStock" | "bestSeller" | "lowStock";
    label: string;
  }> = [
    { value: "outOfStock", label: "Out of Stock" },
    { value: "bestSeller", label: "Best Seller" },
    { value: "lowStock", label: "Low Stock" },
  ];

  const isMobile = useWindowSize();
  const [documents, setDocuments] = useState<Document[]>(
    Array.isArray(documentsList) ? documentsList : [],
  );

  const dispatch = useDispatch<AppDispatch>();

  const [, setIsDeleting] = useState(false);

  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [duplicateData, setDuplicateData] =
    useState<Partial<InventoryFormData> | null>(null);
  const [duplicateModalKey, setDuplicateModalKey] = useState(0);

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>("");
  const [flashedItemId, setFlashedItemId] = useState<string | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const categoryLeadingChips = inventoryFilterOptions.map((option) => ({
    key: option.value,
    label: option.label,
    selected: inventoryFilter === option.value,
    onClick: () =>
      onInventoryFilterChange(
        inventoryFilter === option.value ? "all" : option.value,
      ),
  }));

  const urlToFile = async (
    url: string,
    fallbackName: string,
  ): Promise<File> => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Failed to load image from existing URL (${response.status}).`,
      );
    }
    const blob = await response.blob();
    const mimeType = blob.type || "image/jpeg";
    const ext = mimeType.includes("png")
      ? "png"
      : mimeType.includes("webp")
        ? "webp"
        : "jpg";
    const safeBase = (fallbackName || "inventory-item")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40);
    return new File([blob], `${safeBase || "inventory-item"}.${ext}`, {
      type: mimeType,
    });
  };

  const getCategoryLabel = (item: Document) => {
    const catId = item.categoryId;
    const catIdName =
      catId && typeof catId === "object" ? catId.name : undefined;
    const raw = item.categoryName ?? catIdName ?? item.category;
    return (typeof raw === "string" ? raw.trim() : "") || "Uncategorized";
  };

  const isLowStock = (item: Document) => {
    if (item.__isLowStock === true) return true;

    const quantity =
      typeof item.quantity === "number"
        ? item.quantity
        : Number(item.quantity) || 0;

    const nestedEnabled = !!item.lowStock?.enabled;
    const nestedThreshold =
      typeof item.lowStock?.threshold === "number"
        ? item.lowStock.threshold
        : undefined;

    if (
      nestedEnabled &&
      nestedThreshold !== undefined &&
      quantity <= nestedThreshold
    ) {
      return true;
    }

    const flatEnabled = item.lowStockEnabled === true;
    const flatThreshold =
      typeof item.lowStockThreshold === "number"
        ? item.lowStockThreshold
        : undefined;

    return (
      flatEnabled && flatThreshold !== undefined && quantity <= flatThreshold
    );
  };

  const handleImageClick = (url: string) => {
    setSelectedImageUrl(url);
    setImageModalOpen(true);
  };
  useEffect(
    () => () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    },
    [],
  );

  const incrementFromCard = (item: Document) => {
    onIncrementQuantity(item);
    setFlashedItemId(item._id);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashedItemId(null), 180);
  };

  const cardKeyboardHandler = (
    event: React.KeyboardEvent<HTMLDivElement>,
    item: Document,
  ) => {
    if (!quantityEditMode) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      incrementFromCard(item);
    }
  };

  const renderQuantityEditor = (item: Document) => {
    const staged = getStagedQuantity(item);
    const original = getOriginalQuantity(item);
    const dirty = isQuantityDirty(item);
    const conflicted = isQuantityConflicted(item);
    return (
      <QuantityEditor
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <QuantityButton
          type="button"
          aria-label={`Decrease ${item.itemName} quantity`}
          onClick={(event) => {
            event.stopPropagation();
            onDecrementQuantity(item);
          }}
          disabled={staged <= 0}
        >
          <FiMinus />
        </QuantityButton>
        <QuantityInput
          aria-label={`${item.itemName} quantity`}
          inputMode="numeric"
          type="number"
          min={0}
          step={1}
          value={staged}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (Number.isSafeInteger(next) && next >= 0) {
              onQuantityInput(item, next);
            }
          }}
        />
        <QuantityButton
          type="button"
          aria-label={`Increase ${item.itemName} quantity`}
          onClick={(event) => {
            event.stopPropagation();
            onIncrementQuantity(item);
          }}
        >
          <FiPlus />
        </QuantityButton>
        {dirty && !conflicted && (
          <QuantityDelta>
            {original} → {staged} ({staged - original >= 0 ? "+" : ""}
            {staged - original})
          </QuantityDelta>
        )}
        {conflicted && (
          <ConflictBox>
            <strong>Quantity changed while you were editing</strong>
            <span>
              Started at {original}; current inventory is {Number(item.quantity) || 0};
              your pending quantity is {staged}.
            </span>
            <span>
              <ConflictButton
                type="button"
                onClick={() => onUseCurrentQuantity(item)}
              >
                Use current
              </ConflictButton>
              <ConflictButton
                type="button"
                onClick={() => onKeepPendingQuantity(item)}
              >
                Keep pending
              </ConflictButton>
            </span>
          </ConflictBox>
        )}
      </QuantityEditor>
    );
  };
  useEffect(() => {
    const loadData = () => {
      if (Array.isArray(documentsList)) {
        // ✅ Creates a shallow copy of the array (and deep copy if needed)
        setDocuments(structuredClone(documentsList));
      }
    };

    loadData();
  }, [documentsList]);
  const handleDelete = async () => {
    if (!deleteTargetId) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteInventoryItem(deleteTargetId));
      setDeleteModalOpen(false);
      setDeleteTargetId(null);
      await onRefresh();
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDuplicateOpen = (item: Document) => {
    const categoryId =
      typeof item.categoryId === "object"
        ? item.categoryId?._id || ""
        : item.categoryId || "";

    const transformed: Partial<InventoryFormData> = {
      itemName: `${item.itemName}`,
      businessName: item.businessName || "",
      brand: item.brand || "",
      quantity: item.quantity?.toString() || "0",
      costPerUnit: item.costPerUnit?.toString() || "0",
      categoryId,
      defaultSellingPrice: item.defaultSellingPrice?.toString() || "0",
      sku: "",
      imageUrl: item.imageUrl || "",
      variants: (item.variants || []).map(({ attribute, value }) => ({
        attribute,
        value,
      })),
      notes: item.notes || "",
    };

    setDuplicateData(transformed);
    setDuplicateModalKey((current) => current + 1);
    setAddModalOpen(true);
  };

  const renderPaginationBar = () => {
    const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
    const pageNumbers = isMobile
      ? Array.from(
          new Set([
            1,
            currentPage - 1,
            currentPage,
            currentPage + 1,
            totalPages,
          ]),
        )
          .filter((n) => n >= 1 && n <= totalPages)
          .sort((a, b) => a - b)
      : Array.from({ length: totalPages }, (_, i) => i + 1);

    return (
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-3 md:px-4 py-3 border-t text-sm text-gray-700 w-full">
        <span>
          Showing {(currentPage - 1) * perPage + 1} to{" "}
          {Math.min(currentPage * perPage, totalCount)} of {totalCount} results
        </span>

        <div className="flex flex-wrap items-center gap-1 w-full md:w-auto">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-2.5 py-1.5 md:px-2 md:py-1 rounded border min-w-[40px] md:min-w-0 text-center ${
              currentPage === 1
                ? "text-gray-400 cursor-not-allowed"
                : "hover:bg-gray-100"
            }`}
          >
            &lt;
          </button>

          {pageNumbers.map((num, idx) => {
            const showEllipsis =
              isMobile && idx > 0 && num - pageNumbers[idx - 1] > 1;
            return (
              <span key={`p-${num}`}>
                {showEllipsis && <span className="px-1 text-gray-400">…</span>}
                <button
                  onClick={() => onPageChange(num)}
                  className={`px-2.5 py-1.5 md:px-2 md:py-1 rounded border min-w-[40px] md:min-w-0 text-center ${
                    num === currentPage
                      ? "text-[#2563eb] font-bold"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {num}
                </button>
              </span>
            );
          })}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-2.5 py-1.5 md:px-2 md:py-1 rounded border min-w-[40px] md:min-w-0 text-center ${
              currentPage === totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "hover:bg-gray-100"
            }`}
          >
            &gt;
          </button>
        </div>
      </div>
    );
  };
  return (
    <Container>
      <HeaderBar className="w-full grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] items-start md:items-center gap-3 px-2 md:px-4 text-black">
        <div className="w-full">
          <div className="w-full overflow-x-auto">
            <CategoryChips
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={onCategoryChange}
              leadingChips={categoryLeadingChips}
            />
          </div>
        </div>

        {/* Right Controls */}
        <div className="w-full md:w-auto flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center gap-2 md:justify-end">
          {/* View toggle buttons */}
          <div className="flex items-center gap-1 bg-gray-100 px-1 py-0.5 rounded-lg shadow-inner self-start">
            <button
              onClick={onToggleView}
              className={`w-8 h-8 flex items-center justify-center rounded-md transition ${
                viewMode === "list"
                  ? "bg-white shadow text-black"
                  : "text-gray-400 hover:bg-white"
              }`}
              title="List View"
            >
              <FiList size={16} />
            </button>
            <button
              onClick={onToggleView}
              className={`w-8 h-8 flex items-center justify-center rounded-md transition ${
                viewMode === "card"
                  ? "bg-white shadow text-black"
                  : "text-gray-400 hover:bg-white"
              }`}
              title="Card View"
            >
              <FiGrid size={16} />
            </button>
          </div>

          {/* Search */}
          <div className="relative w-full md:w-72">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search item or brand..."
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            {searchTerm ? (
              <FiX
                className="absolute left-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500"
                size={18}
                onClick={() => onSearchChange("")}
                title="Clear search"
              />
            ) : (
              <FiSearch
                className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
            )}
          </div>

          {/* Refresh */}
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className={`px-3 py-2 border border-gray-300 rounded-md text-sm flex items-center gap-1 justify-center ${
              isRefreshing
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-100 text-black"
            }`}
          >
            <FiRefreshCcw className={isRefreshing ? "animate-spin" : ""} />
            Refresh
          </button>

          {/* Add Inventory */}

          <UploadButton
            onClick={() => {
              if (!allowAddInventory) return;
              setAddModalOpen(true);
            }}
            disabled={isRefreshing || !allowAddInventory}
          />
          {/* {accessToken && (
            <>
              <DownloadInventoryImagesButton apiBase={BASE_URL} debug />
              <DownloadInventoryJsonButton
                facebookPage="beeline3d"
                publicImagesPrefix="/images/"
                includeItemsWithoutImages={false}
                debug
              />
            </>
          )}

          {accessToken && (
            <DownloadInventoryImagesButton apiBase={BASE_URL} debug />
          )} */}
        </div>
      </HeaderBar>
      <AddInventoryModal
        key={duplicateModalKey}
        open={isAddModalOpen}
        onClose={() => {
          setAddModalOpen(false);
          setDuplicateData(null);
        }}
        initialData={duplicateData || undefined}
        onSave={async (form: InventoryFormData) => {
          const token = localStorage.getItem("access_token");

          try {
            if (!form.categoryId) throw new Error("Please select a category.");

            let imageFile = form.imageFile;
            if (!imageFile && form.imageUrl) {
              try {
                imageFile = await urlToFile(form.imageUrl, form.itemName);
              } catch (e) {
                throw new Error(
                  "Could not reuse the existing image. Please upload an image file before saving.",
                );
              }
            }
            if (!imageFile) {
              throw new Error("Image file is required.");
            }

            const payload = {
              ...form,
              quantity: parseFloat(form.quantity),
              costPerUnit: parseFloat(form.costPerUnit),
              defaultSellingPrice: parseFloat(form.defaultSellingPrice),
            };
            delete payload.imageFile;

            await uploadInventoryImage(imageFile, payload, token || "");
            setAddModalOpen(false);
            setDuplicateData(null);
            await onRefresh();
          } catch (err) {
            console.error("Failed to upload inventory:", err);
            if (typeof window !== "undefined") {
              window.alert(
                err instanceof Error && err.message
                  ? err.message
                  : "Failed to add inventory item. Please try again.",
              );
            }
            throw err;
          }
        }}
      />
      {renderPaginationBar()}
      {isLoading ? (
        <SkeletonLoader />
      ) : viewMode === "card" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 p-4">
          {documents.map((item) => (
            <HoverContainer
              key={item._id}
              $quantityEditing={quantityEditMode}
              $flashed={flashedItemId === item._id}
              onClick={() =>
                quantityEditMode ? incrementFromCard(item) : onEditItem(item)
              }
              onKeyDown={(event) => cardKeyboardHandler(event, item)}
              role={quantityEditMode ? "button" : undefined}
              tabIndex={quantityEditMode ? 0 : undefined}
              aria-label={quantityEditMode ? `Add one to ${item.itemName}` : undefined}
            >
              {!quantityEditMode && allowItemActions && <ActionBar>
                {/* <ActionButton>
              <FiList />
              <span>Mark Read</span>
            </ActionButton> */}
                {/* <ActionButton>
              <LuArrowBigUp />
              <span>Flag</span>
            </ActionButton> */}
                <ActionButton
                  className="edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditItem(item);
                  }}
                >
                  <FiEdit />
                  <span>Edit</span>
                </ActionButton>
                <ActionButton
                  className="delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTargetId(item._id);
                    setDeleteModalOpen(true);
                  }}
                >
                  <FiTrash2 />
                  <span>Delete</span>
                </ActionButton>
                <ActionButton
                  className="duplicate"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicateOpen(item);
                  }}
                >
                  <FiPlus />
                  <span>Duplicate</span>
                </ActionButton>
              </ActionBar>}
              {isLowStock(item) && <LowStockBadge>LOW</LowStockBadge>}

              <CardContent>
                <ImageWrapper>
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.itemName}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (quantityEditMode) {
                          incrementFromCard(item);
                          return;
                        }
                        handleImageClick(item.imageUrl!);
                      }}
                      style={{ cursor: quantityEditMode ? "copy" : "pointer" }}
                    />
                  ) : (
                    <InventoryImagePlaceholder name={item.itemName} />
                  )}
                </ImageWrapper>
                <div className="p-2">
                  <h3 className="text-sm font-semibold truncate">
                    {item.itemName}
                  </h3>
                  <p className="text-xs text-gray-500 truncate">
                    {item.brand || "No brand"}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {getCategoryLabel(item)}
                  </p>
                  {!quantityEditMode && <p className="text-xs text-gray-500">
                    Qty: {item.quantity} | $
                    {item.defaultSellingPrice?.toFixed(2) ?? "0.00"}
                  </p>}
                  {quantityEditMode && renderQuantityEditor(item)}
                </div>
              </CardContent>
            </HoverContainer>
          ))}
        </div>
      ) : (
        documents.map((item: Document) => (
          <HoverContainer
            key={item._id}
            $quantityEditing={quantityEditMode}
            $flashed={flashedItemId === item._id}
            onClick={() =>
              quantityEditMode ? incrementFromCard(item) : onEditItem(item)
            }
            onKeyDown={(event) => cardKeyboardHandler(event, item)}
            role={quantityEditMode ? "button" : undefined}
            tabIndex={quantityEditMode ? 0 : undefined}
            aria-label={quantityEditMode ? `Add one to ${item.itemName}` : undefined}
          >
            <DocumentContainer key={item._id}>
              {isLowStock(item) && <LowStockBadge>LOW</LowStockBadge>}
              {!isMobile && (
                <ImageContainer>
                  {item.imageUrl ? (
                    <Image
                      src={item.imageUrl}
                      alt={item.itemName}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (quantityEditMode) {
                          incrementFromCard(item);
                          return;
                        }
                        handleImageClick(item.imageUrl!);
                      }}
                      style={{ cursor: quantityEditMode ? "copy" : "pointer" }}
                    />
                  ) : (
                    <InventoryImagePlaceholder name={item.itemName} />
                  )}
                </ImageContainer>
              )}
              {isMobile && (
                <MobileImageWrapper>
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.itemName}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (quantityEditMode) {
                          incrementFromCard(item);
                          return;
                        }
                        handleImageClick(item.imageUrl!);
                      }}
                      style={{ cursor: quantityEditMode ? "copy" : "pointer" }}
                    />
                  ) : (
                    <InventoryImagePlaceholder name={item.itemName} />
                  )}
                </MobileImageWrapper>
              )}
              <Info>
                <Name>{item.itemName}</Name>
                <DetailContainer>
                  <Stats>Category: {getCategoryLabel(item)}</Stats>
                  <Stats>Brand: {item.brand || "N/A"}</Stats>
                  {/* <Stats>Status: {item.status}</Stats> */}
                  <Stats>SKU: {item.sku || "N/A"}</Stats>
                </DetailContainer>
                {!quantityEditMode && <StatsContainer>
                  <Stat>Quantity: {item.quantity}</Stat>
                  <Stat style={{ marginLeft: 20 }}>
                    Price: ${item.defaultSellingPrice ?? 0}
                  </Stat>
                  {/* <Stat style={{ marginLeft: 20 }}>
                    Cost: ${item.costPerUnit ?? 0}
                  </Stat> */}
                </StatsContainer>}
                {quantityEditMode && renderQuantityEditor(item)}
              </Info>
            </DocumentContainer>

            {!quantityEditMode && allowItemActions && <ActionBar>
              {/* <ActionButton>
                <FiList />
                <span>Mark Read</span>
              </ActionButton> */}
              {/* <ActionButton>
                <LuArrowBigUp />
                <span>Flag</span>
              </ActionButton> */}
              <ActionButton
                className="edit"
                onClick={(e) => {
                  onEditItem(item);
                  e.stopPropagation();
                }}
              >
                <FiEdit />
                <span>Edit</span>
              </ActionButton>
              <ActionButton
                className="delete"
                onClick={(e) => {
                  setDeleteTargetId(item._id);
                  setDeleteModalOpen(true);
                  e.stopPropagation();
                }}
              >
                <FiTrash2 />
                <span>Delete</span>
              </ActionButton>
              <ActionButton
                className="duplicate"
                onClick={(e) => {
                  handleDuplicateOpen(item);
                  e.stopPropagation();
                }}
              >
                <FiPlus />
                <span>Duplicate</span>
              </ActionButton>
            </ActionBar>}
          </HoverContainer>
        ))
      )}

      <ImageViewerModal
        open={isImageModalOpen}
        imageUrl={selectedImageUrl}
        onClose={() => setImageModalOpen(false)}
      />
      {documents.length === 0 && (
        <NoDocumentsContainer>
          <Text>No Uploads Yet </Text>
          {allowAddInventory && (
            <StyledUploadButton type="button" onClick={() => setAddModalOpen(true)}>
              {addInventoryLabel}
            </StyledUploadButton>
          )}
          {allowAddInventory && (
            <StyledBulkImportButton to="/import">
              Add bulk inventory or items from Square
            </StyledBulkImportButton>
          )}
        </NoDocumentsContainer>
      )}

      {renderPaginationBar()}
      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
      />
    </Container>
  );
};

export default DocumentsList;

const HeaderBar = styled.div`
  margin-top: 0;
  padding-top: 0.5rem;
  padding-bottom: 0.25rem;
`;

const NoDocumentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: auto;
  padding: 30px;
`;
const ActionBar = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.25rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 4px 6px;
  opacity: 0;
  transition: none;

  z-index: 10;

  @media (max-width: 768px) {
    position: static;
    opacity: 1;
    width: 100%;
    justify-content: flex-end;
    margin: 6px 0 0;
    background: transparent;
    box-shadow: none;
    padding: 0;
  }
`;

const ActionButton = styled.button`
  position: relative;
  all: unset;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease-in-out;

  svg {
    color: #4a5568;
    width: 16px;
    height: 16px;
    transition: color 0.2s ease-in-out;
  }

  span {
    position: absolute;
    top: -36px;
    background-color: white;
    color: #1a202c;
    padding: 4px 10px;
    font-size: 0.75rem;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    opacity: 0;
    transform: translateY(6px);
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
    white-space: nowrap;
    pointer-events: none;
    z-index: 20;
  }
  &:hover span {
    opacity: 1;
    transform: translateY(0);
  }

  &.edit:hover svg {
    color: #ecc94b; /* yellow.500 */
  }

  &.delete:hover svg {
    color: #f56565; /* red.500 */
  }
  &.duplicate:hover svg {
    color: #48bb78; /* green.400 */
  }

  @media (max-width: 768px) {
    padding: 8px;
    background: #f3f4f6;
    border-radius: 10px;

    span {
      display: none;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  border-radius: 10px;
  background-color: #fff;

  @media (max-width: 768px) {
    padding: 0 0.25rem;
    margin-top: 0.25rem;
  }
`;

const HoverContainer = styled.div<{
  $quantityEditing?: boolean;
  $flashed?: boolean;
}>`
  position: relative;
  cursor: ${({ $quantityEditing }) => ($quantityEditing ? "copy" : "pointer")};
  outline: ${({ $quantityEditing }) =>
    $quantityEditing ? "1px solid #bfdbfe" : "none"};
  border-radius: ${({ $quantityEditing }) => ($quantityEditing ? "12px" : "0")};
  background: ${({ $flashed }) => ($flashed ? "#dbeafe" : "transparent")};
  transition: background-color 120ms ease, transform 120ms ease;
  transform: ${({ $flashed }) => ($flashed ? "scale(0.995)" : "none")};

  &:focus-visible {
    outline: 3px solid #60a5fa;
    outline-offset: 2px;
  }

  &:hover ${ActionBar} {
    opacity: 1;
    pointer-events: auto;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none;
  }
`;

const QuantityEditor = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
  margin-top: 0.55rem;
  cursor: default;
`;

const QuantityButton = styled.button`
  width: 42px;
  height: 42px;
  border: 1px solid #93c5fd;
  border-radius: 10px;
  background: #eff6ff;
  color: #1d4ed8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.1rem;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 82px;
  height: 42px;
  border: 1px solid #94a3b8;
  border-radius: 10px;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 800;
  color: #0f172a;
`;

const QuantityDelta = styled.span`
  color: #1d4ed8;
  background: #dbeafe;
  border-radius: 9999px;
  padding: 0.25rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 700;
`;

const ConflictBox = styled.div`
  flex-basis: 100%;
  display: grid;
  gap: 0.3rem;
  border: 1px solid #f59e0b;
  background: #fffbeb;
  color: #92400e;
  border-radius: 10px;
  padding: 0.55rem;
  font-size: 0.76rem;
`;

const ConflictButton = styled.button`
  border: 1px solid #d97706;
  background: #fff;
  color: #92400e;
  border-radius: 8px;
  padding: 0.3rem 0.55rem;
  margin-right: 0.4rem;
  cursor: pointer;
  font-weight: 700;
`;

const LowStockBadge = styled.span`
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 11;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #ef4444;
  color: #fff;
  padding: 2px 7px;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: 0;
  pointer-events: none;
`;

const DocumentContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: auto auto 2fr 1fr;
  gap: 10px;
  align-items: start;
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
  box-shadow: 0 0 0 transparent;
  transition:
    background-color 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;

  ${HoverContainer}:hover & {
    background-color: #f7fafc; /* soft gray */
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 12px;
    border-radius: 14px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
    margin-bottom: 10px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  ${HoverContainer}:hover & {
    background-color: #f7fafc; /* soft gray */
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
`;

const ImageWrapper = styled.div`
  height: 176px;
  background-color: #f3f4f6;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImageContainer = styled.div`
  width: 150px;
  height: 150px;
  margin-right: 20px;
  border-radius: 5px;
  overflow: hidden;
  background-color: #eee;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MobileImageWrapper = styled.div`
  width: 100%;
  height: 140px;
  border-radius: 10px;
  overflow: hidden;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 0;
  min-width: 0;
`;

const Name = styled.h2`
  margin: 0;
  color: #1a202c;
  font-size: 1.25rem; /* ~20px */
  font-weight: 700;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

const Stats = styled.div`
  font-size: 0.85rem; /* ~13.6px */
  color: #4a5568;
  margin-top: 4px;
  word-break: break-word;
`;

const StatsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 6px 12px;
  }
`;

const DetailContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-self: space-between;
  gap: 50px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;
  }
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.85rem; /* match Stats */
  color: #4a5568;
`;

const Text = styled.h2`
  font-size: 20px;
  color: #a0aec0;
  margin-bottom: 20px;
`;
const StyledBulkImportButton = styled(Link)`
  background-color: #eff6ff;
  color: #2563eb;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: bold;
  margin: 12px 10px 0 0;
  text-align: center;
  text-decoration: none;

  &:hover {
    background-color: #dbeafe;
  }
`;

const StyledUploadButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  border: none;
  margin-right: 10px;
  text-align: center;

  &:hover {
    background-color: #0056b3;
  }
`;
